import React, { useState } from "react";
import { Button } from "../ui/button";
import { ArrowLeft, Mail, CheckCircle2, RefreshCw } from "lucide-react";
import { forgotPassword } from "@/services/auth";

interface CheckEmailProps {
  onBack?: () => void; // callback to go back to login screen
  email?: string; // Email address to display
}

const CheckEmail: React.FC<CheckEmailProps> = ({ onBack, email = "user@gmail.com" }) => {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = async () => {
    if (!email) return;
    
    setIsResending(true);
    setResendSuccess(false);
    setResendError(null);
    
    try {
      await forgotPassword(email);
      setResendSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error: unknown) {
      const err = error as Error;
      setResendError(err.message || "Failed to resend email. Please try again.");
      setTimeout(() => setResendError(null), 5000);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-8 mt-8 text-center">
      {/* Email Icon with Animation */}
      <div className="relative">
        <div className="absolute inset-0 bg-primary/10 rounded-full blur-xl animate-pulse" />
        <div className="relative bg-gradient-to-br from-primary/20 to-primary/10 p-6 rounded-full">
          <Mail className="h-16 w-16 text-primary" />
        </div>
      </div>

      {/* Header Section */}
      <div className="flex flex-col gap-3 max-w-md">
        <h1 className="text-3xl font-bold text-foreground">
          Check Your Email
        </h1>
        <p className="text-muted-foreground text-base leading-relaxed">
          We&apos;ve sent a password reset link to
        </p>
        <div className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <Mail className="h-4 w-4 text-primary" />
          <span className="text-primary font-semibold text-base">{email}</span>
        </div>
      </div>

      {/* Instructions */}
      <div className="flex flex-col gap-3 max-w-md">
        <div className="flex items-start gap-3 text-left bg-muted/50 p-4 rounded-lg border">
          <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">What to do next:</p>
            <ul className="space-y-1.5 list-disc list-inside">
              <li>Check your inbox for an email from us</li>
              <li>Click on the password reset link in the email</li>
              <li>The link will expire in 1 hour for security</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Resend Email Section */}
      <div className="flex flex-col items-center gap-2 w-full max-w-md pt-2">
        <p className="text-sm text-muted-foreground">
          Didn&apos;t receive the email?
        </p>
        <Button
          type="button"
          onClick={handleResend}
          variant="ghost"
          className="h-10 text-primary hover:text-primary/80 hover:bg-primary/5"
          disabled={isResending || resendSuccess || !email}
        >
          {isResending ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : resendSuccess ? (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
              Email sent!
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Resend Email
            </>
          )}
        </Button>
        {resendError && (
          <p className="text-sm text-destructive mt-1">{resendError}</p>
        )}
      </div>

      {/* Back to Login */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center justify-center gap-2 mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
        <span>Back to Login</span>
      </button>
    </div>
  );
};

export default CheckEmail;
